import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { theme } from '../theme';
import { PinIcon } from '../Icons';
import PageBackground from './PageBackground';

function HotelDealScreen() {
  const location = useLocation();
  const navigate = useNavigate();
  const pin = location.state?.pin;

  if (!pin) {
    // Direct navigation without a pin in state (e.g. a bookmarked/shared
    // link, or a refresh that lost router state) — nothing to show,
    // send back to the dashboard rather than rendering a broken page.
    navigate('/dashboard');
    return null;
  }

  const budgetCurrency = pin.currency || 'CAD';
  const priceCurrency = pin.last_price_currency || budgetCurrency;
  const currencyMismatch = priceCurrency !== budgetCurrency;

  const nights = (pin.check_in_date && pin.check_out_date)
    ? Math.round((new Date(pin.check_out_date) - new Date(pin.check_in_date)) / 86400000)
    : null;

  const buildBookingComHotelUrl = () => {
    // Booking.com's standard accommodation search — a stable, long-lived
    // URL pattern (not the newer flights.booking.com path used for
    // flights). `selected_currency` is Booking.com's documented param
    // for their stays search specifically (distinct from flights' actual
    // param name, `currency_code`, found the hard way earlier today) —
    // worth a live check after deploying, same caveat as every other
    // partner link in this app.
    const params = new URLSearchParams();
    params.set('ss', pin.location);
    if (pin.check_in_date) params.set('checkin', pin.check_in_date);
    if (pin.check_out_date) params.set('checkout', pin.check_out_date);
    params.set('group_adults', pin.num_guests || 2);
    params.set('no_rooms', pin.num_rooms || 1);
    params.set('selected_currency', budgetCurrency);
    return `https://www.booking.com/searchresults.html?${params.toString()}`;
  };

  const getFreshnessState = (timestamp) => {
    if (!timestamp) return { label: 'Checking...', color: theme.textMuted, warn: false };
    const minutes = Math.floor((new Date() - new Date(timestamp)) / 60000);
    const timeStr = new Date(timestamp).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    if (minutes < 30) return { label: `Checked at ${timeStr}`, color: theme.freshnessOk || theme.success, warn: false };
    return { label: `Checked at ${timeStr} — verify before booking`, color: theme.danger, warn: true };
  };
  const freshness = getFreshnessState(pin.last_checked);

  return (
    <div style={{ minHeight: '100vh', paddingBottom: '40px' }}>
      <PageBackground variant="calm" />
      <div style={{ background: theme.structural, color: theme.cream, padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontFamily: theme.fontDisplay, fontSize: '20px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px' }}>
          <PinIcon color={theme.action} />
          FlyAtlast
        </div>
        <div
          onClick={() => navigate('/dashboard')}
          style={{ background: theme.action, color: theme.cream, padding: '8px 16px', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}
        >
          ← Back to Dashboard
        </div>
      </div>

      <div style={{ maxWidth: '600px', margin: '0 auto', padding: '32px 20px' }}>
        <div style={{
          background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: '12px',
          padding: '16px 18px', marginBottom: '24px'
        }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: theme.structural, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px' }}>
            Trip Details
          </div>
          <div style={{ fontSize: '14px', color: theme.textPrimary, lineHeight: 1.7 }}>
            <div>Location: <strong>{pin.location}</strong></div>
            <div>Check-in → Check-out: <strong>{pin.check_in_date} → {pin.check_out_date}</strong>{nights ? ` (${nights} night${nights !== 1 ? 's' : ''})` : ''}</div>
            <div>Rooms: <strong>{pin.num_rooms || 1}</strong>, Guests: <strong>{pin.num_guests || 1}</strong></div>
            <div>Budget: <strong>{pin.budget} {budgetCurrency}</strong> (total for the stay)</div>
          </div>
        </div>

        <div style={{ textAlign: 'center' }}>
          <div style={{ width: '60px', height: '60px', borderRadius: '50%', background: theme.structural, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
            <PinIcon size={28} color={theme.cream} />
          </div>
          <h1 style={{ fontFamily: theme.fontDisplay, fontSize: '24px', fontWeight: 700, color: theme.textPrimary, margin: 0 }}>
            At last! {pin.location} is a deal
          </h1>
          <p style={{ fontSize: '13px', color: freshness.color, fontWeight: freshness.warn ? 'bold' : 'normal', marginTop: '8px' }}>
            {freshness.warn && '⚠ '}{freshness.label}
          </p>

          <div style={{ marginTop: '20px' }}>
            <div style={{ fontFamily: theme.fontDisplay, fontSize: '42px', fontWeight: 700, color: theme.action }}>
              {pin.last_price}
              <span style={{ fontSize: '16px', fontWeight: 700, marginLeft: '6px', color: theme.textMuted }}>
                {priceCurrency}
              </span>
            </div>

            {currencyMismatch && pin.last_price_converted != null && (() => {
              const convertedDiff = pin.budget - pin.last_price_converted;
              const isUnder = convertedDiff >= 0;
              return (
                <div style={{ fontSize: '14px', color: isUnder ? theme.success : '#c0392b', fontWeight: 700, marginTop: '6px' }}>
                  {isUnder
                    ? `≈ saves ${convertedDiff.toFixed(0)} ${budgetCurrency} under your ${pin.budget} ${budgetCurrency} budget`
                    : `≈ ${Math.abs(convertedDiff).toFixed(0)} ${budgetCurrency} over your ${pin.budget} ${budgetCurrency} budget`}
                </div>
              );
            })()}

            {currencyMismatch && pin.last_price_converted == null && (
              <div style={{ fontSize: '13px', color: '#c0392b', fontWeight: 600, marginTop: '6px' }}>
                ⚠ Priced in {priceCurrency}, but your budget is set in {budgetCurrency} — couldn't convert just now. Check the actual amount before booking.
              </div>
            )}

            {!currencyMismatch && (
              <div style={{ fontSize: '14px', color: pin.budget - pin.last_price >= 0 ? theme.success : '#c0392b', fontWeight: 700, marginTop: '6px' }}>
                {pin.budget - pin.last_price >= 0
                  ? `saves ${(pin.budget - pin.last_price).toFixed(0)} ${budgetCurrency} under your ${pin.budget} ${budgetCurrency} budget`
                  : `${Math.abs(pin.budget - pin.last_price).toFixed(0)} ${budgetCurrency} over your ${pin.budget} ${budgetCurrency} budget`}
              </div>
            )}

            {(pin.check_count || 0) > 0 && (
              <div style={{ fontSize: '12px', color: theme.textMuted, marginTop: '8px' }}>
                Checked {pin.check_count} time{pin.check_count !== 1 ? 's' : ''}
              </div>
            )}
          </div>

          <a
            href={buildBookingComHotelUrl()}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'block', marginTop: '24px', background: theme.structural, color: theme.cream,
              textDecoration: 'none', fontFamily: theme.fontDisplay, fontWeight: 700, fontSize: '16px',
              padding: '16px', borderRadius: '12px'
            }}
          >
            Search on Booking.com →
          </a>
          <p style={{ fontSize: '12px', color: theme.textMuted, marginTop: '10px' }}>
            Opens a live search for your dates and guests — Duffel Stays doesn't yet support direct booking on our account, so this is where you'd actually complete the reservation. Prices may differ from above.
          </p>
        </div>
      </div>
    </div>
  );
}

export default HotelDealScreen;